import { Box, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { HttpStatusCode } from 'axios';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAlertContext } from '../../../../context/AlertProvider';
import { useApi } from '../../../../context/ApiProvider';
import { CoFinancingInstitutionInterface } from '../../../../shared/type/coFinancingInstitution.type';
import CustomAlert from '../../../CustomAlert/CustomAlert';
import CustomChakraButton from '../../../CustomChakraButton/CustomChakraButton';

import './ListOfCoFinancingInstitutions.css';
import IconComponent from '../../../IconComponent/IconComponent';
import AddEditCoFinancialInstitution from './AddEditCoFinancialInstitution/AddEditCoFinancialInstitution';
import { DeletedElementInterface } from '../../../../shared/type/deletedElement.type';
import { StickyScrollableTableContainer } from '../../../DataTable/StickyScrollableTableContainer';

const defaultDeleteElement = {
  id: 0,
  isOpen: false
};

const ListOfCoFinancingInstitutions = () => {
  const { apiTenantCoFinancingInstitutionController } = useApi();
  const columnHelper = createColumnHelper<CoFinancingInstitutionInterface>();
  const { id: tenantId } = useParams();
  const { setAlertProperties, setShow } = useAlertContext();

  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState<CoFinancingInstitutionInterface[]>([]);
  const [deleteElement, setDeleteElement] = useState<DeletedElementInterface>(defaultDeleteElement);
  const [addEditPopUp, setAddEditPopUp] = useState<{ isOpen: boolean; mode: 'add' | 'edit' }>({
    isOpen: false,
    mode: 'add'
  });
  const [editedItem, setEditedItem] = useState<CoFinancingInstitutionInterface>();

  const fetchInstitutions = async () => {
    setIsLoading(true);
    try {
      const response = await apiTenantCoFinancingInstitutionController('').get(`/${tenantId}`);
      setList(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstitutions();
  }, [tenantId]);

  const columns = [
    columnHelper.accessor('name', {
      header: 'Nazwa instytucji',
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 w-[300px] text-wrap">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('nip', {
      header: 'NIP',
      cell: (info) => <p className="text-sm text-grayLight-600 w-32">{info.getValue()}</p>
    }),
    columnHelper.accessor('street', {
      header: 'Ulica',
      cell: (info) => <p className="text-sm text-grayLight-600 w-32 text-wrap">{info.getValue()}</p>
    }),
    columnHelper.accessor('postcode', {
      header: 'Kod',
      cell: (info) => <p className="text-sm text-grayLight-600 w-[60px]">{info.getValue()}</p>
    }),
    columnHelper.accessor('city', {
      header: 'Miasto',
      cell: (info) => <p className="text-sm text-grayLight-600 w-32 text-wrap">{info.getValue()}</p>
    }),
    columnHelper.accessor('phone', {
      header: 'Telefon',
      cell: (info) => <p className="text-sm text-grayLight-600 w-32">{info.getValue()}</p>
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Edycja',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex gap-4">
            <button
              className="text-sm font-semibold text-grayLight-600"
              onClick={() => setDeleteElement({ id: item.id, isOpen: true })}>
              Usuń
            </button>
            <button
              className="text-sm font-semibold text-grayWarm-950"
              onClick={() => {
                setEditedItem(item);
                setAddEditPopUp({ isOpen: true, mode: 'edit' });
              }}>
              Edytuj
            </button>
          </div>
        );
      }
    })
  ];

  return (
    <section className="ListOfCoFinancingInstitutions p-spacing-xl w-full">
      <Box mb={6} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <Text fontSize="xl" fontWeight="semibold" color="gray.900">
            Instytucje dofinansowujące
          </Text>
          <Text fontSize="sm" color="gray.600">
            Lista dofinansowań MOPR/MOPS/OPS
          </Text>
        </div>
        <CustomChakraButton
          size="md"
          hierarchy="primary"
          iconPosition="left"
          iconColor="#292524"
          icon="plus"
          buttonProps={{
            type: 'button',
            onClick: () => setAddEditPopUp({ isOpen: true, mode: 'add' })
          }}>
          Dodaj instytucję dofinansowującą
        </CustomChakraButton>
      </Box>

      <StickyScrollableTableContainer
        columns={columns}
        data={list}
        isLoading={isLoading}
        enableSearch={true}
      />

      <CustomAlert
        header="Usunąć instytucję?"
        content="Usunięcie instytucji jest nieodwracalne."
        confirmButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'warning' }}
        confirmButtonText="Usuń"
        onConfirmAction={async () => {
          try {
            const response = await apiTenantCoFinancingInstitutionController('').delete(
              `/${tenantId}/${deleteElement.id}`
            );
            if (response.status === HttpStatusCode.NoContent) {
              setAlertProperties({
                timeout: 9000,
                title: 'Sukces',
                description: 'Poprawnie usunięto instytucję',
                status: 'success'
              });
              setShow(true);
              setDeleteElement(defaultDeleteElement);
              fetchInstitutions();
            }
          } catch (error) {
            setAlertProperties({
              timeout: 9000,
              title: 'Błąd',
              description: 'Nie udało się usunąć instytucji',
              status: 'error'
            });
            setShow(true);
          }
        }}
        cancelButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'secondaryGray' }}
        cancelButtonText="Anuluj"
        onCancelAction={() => {
          setDeleteElement(defaultDeleteElement);
        }}
        handleOpen={deleteElement.isOpen}
        icon={
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-error-100">
            <IconComponent iconName="trash" />
          </div>
        }
      />

      {addEditPopUp.isOpen && (
        <AddEditCoFinancialInstitution
          initData={editedItem}
          mode={addEditPopUp.mode}
          isOpen={addEditPopUp.isOpen}
          onClose={() => {
            setAddEditPopUp({ isOpen: false, mode: 'add' });
            fetchInstitutions();
          }}
        />
      )}
    </section>
  );
};

export default ListOfCoFinancingInstitutions;
